import { computed, inject, type InjectionKey } from 'vue';
import { useValidationErrorsStore, type FieldErrors } from '@/js/stores/validationErrors';
import { __, __untypedExpression, hasExpression } from '@/js/stores/expressions';

export const VALIDATION_SCOPE_KEY: InjectionKey<string> = Symbol('validationScope');

export function useValidationErrors(scope?: string) {
    const resolvedScope = scope ?? inject(VALIDATION_SCOPE_KEY, null);
    if (!resolvedScope) {
        throw new Error(
            'useValidationErrors: no scope provided. Pass an explicit scope or `provide(VALIDATION_SCOPE_KEY, ...)` in an ancestor.'
        );
    }

    const store = useValidationErrorsStore();
    const errors = computed<FieldErrors>(() => store.errors[resolvedScope] ?? {});

    const hasError = (path: string) => (errors.value[path]?.length ?? 0) > 0;

    const errorMessages = (path: string): string[] =>
        (errors.value[path] ?? []).map((message) =>
            hasExpression(message) ? __untypedExpression(message) : message
        );

    const errorMessage = (path: string): string | undefined => errorMessages(path)[0];

    const buildErrorId = (path: string) => `${resolvedScope}-error-${path.replace(/\./g, '-')}`;

    const errorId = (path: string): string | undefined =>
        hasError(path) ? buildErrorId(path) : undefined;

    const clearField = (path: string) => store.clearField(resolvedScope, path);

    return {
        scope: resolvedScope,
        errors,
        hasError,
        errorMessage,
        errorMessages,
        errorId,
        clearField,
    };
}
